import React, { useState } from 'react';

import { isSignatureFieldElement } from '@aline/common';
import { link } from '@aline/routes';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Editor, Transforms } from 'slate';
import { AlineDialog, AlineDialogButtons } from 'src/components';
import { AlineTextarea } from 'src/components/AlineTextarea';
import {
  WorkflowAssignmentAutocomplete,
  WorkflowAssignmentAutocompleteContext,
} from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import type { WorkflowAssignmentSelection } from 'src/components/workflow/WorkflowAssignmentAutocomplete';
import { Apollo } from 'src/gql/generated';
import { useGetCurrentDoc } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  messageContainer: {
    marginTop: theme.spacing(2),
  },
  reassignModal: {
    minWidth: theme.spacing(71),
    [theme.breakpoints.down('sm')]: {
      // Bump inputs to 16px so iOS Safari doesn't auto-zoom on focus.
      '& input, & textarea': {
        fontSize: 16,
      },
      margin: theme.spacing(2),
      minWidth: 0,
      width: `calc(100% - ${theme.spacing(4)})`,
    },
  },
}));

interface ReassignFieldDialogProps {
  onClose: () => void;
  open: boolean;
  recipientId: UUID;
}

export function ReassignFieldDialog({
  onClose,
  open,
  recipientId,
}: ReassignFieldDialogProps): JSX.Element | null {
  const { classes } = useStyles();
  const editor = useEditor();
  const navigate = useNavigate();
  const { document } = useGetCurrentDoc();
  const workflowId = document?.workflow?.id ?? '';
  const [message, setMessage] = useState<string>('');
  const [reassignRecipient, { loading }] = Apollo.useReassignRecipientMutation();
  const [selectedAssignee, setSelectedAssignee] = useState<WorkflowAssignmentSelection | null>(
    null
  );

  if (!document?.workflow?.id) return null;

  function handleClose() {
    setSelectedAssignee(null);
    setMessage('');
    onClose();
  }

  function handleChange(_: React.ChangeEvent<unknown>, value: WorkflowAssignmentSelection | null) {
    // Drop anything the mutation cannot name rather than holding a selection that can't submit.
    setSelectedAssignee(value && getReassignRecipientFields(value) ? value : null);
  }

  async function handleReassign() {
    if (!selectedAssignee) return;

    const fields = getReassignRecipientFields(selectedAssignee);
    if (!fields) return;

    const trimmedMessage = message.trim();
    const { data } = await reassignRecipient({
      refetchQueries: [
        {
          query: Apollo.GetWalkthroughWorkflowDocument,
          variables: { workflowId } satisfies Apollo.GetWalkthroughWorkflowQueryVariables,
        },
      ],
      variables: {
        input: {
          ...fields,
          message: trimmedMessage ? trimmedMessage : null,
          recipientId,
        },
      },
    });
    if (!data) return;

    /**
     * The server cleared these in the stored document, but this editor is still holding the
     * signatures in memory — the walkthrough tab is hidden rather than unmounted on navigation.
     */
    const clearedNodeIds = new Set(data.reassignRecipient.clearedSignatureFieldNodeIds);
    if (clearedNodeIds.size) {
      const sigFieldEntries = Editor.nodes(editor, {
        at: [],
        match: n =>
          isSignatureFieldElement(n) && clearedNodeIds.has(n.fieldId) && Boolean(n.signatureImgId),
        voids: true,
      });

      for (const [, path] of sigFieldEntries) {
        Transforms.setNodes(editor, { signatureImgId: null }, { at: path, voids: true });
      }
    }

    handleClose();
    // Nothing is left for this signer to do on the document, so leave the editor entirely.
    navigate(link.WorkflowReassigned({ workflowId }));
  }

  return (
    <AlineDialog
      classes={{ paper: classes.reassignModal }}
      maxWidth={false}
      open={open}
      title="Reassign Signer?"
      onClose={handleClose}
    >
      <div>
        <Typography className={classes.label}>New Signer</Typography>
        <WorkflowAssignmentAutocomplete
          context={WorkflowAssignmentAutocompleteContext.WorkflowCollaborators}
          value={selectedAssignee}
          workflowId={workflowId}
          onChange={handleChange}
        />
      </div>
      <div className={classes.messageContainer}>
        <Typography className={classes.label}>Message</Typography>
        <AlineTextarea
          maxRows={5}
          minRows={5}
          placeholder="Add a message for the new signer..."
          value={message}
          onChange={e => setMessage(e.target.value)}
        />
      </div>
      <div>
        <AlineDialogButtons
          cancelHandler={handleClose}
          disabled={!selectedAssignee || loading}
          loading={loading}
          submitHandler={handleReassign}
          submitText="Reassign"
        />
      </div>
    </AlineDialog>
  );
}

/**
 * A signer performing the reassign has NoAccess/Viewer workflow permission — not Assignee — so
 * they cannot call addWorkflowPermissions to pre-create a WorkflowPermission for a new
 * collaborator. The reassignRecipient resolver accepts email/assignedUserId/assignedRoleId and
 * creates the permission server-side (authorized by isAssignedToCurrentUser), so the raw
 * selection is held in state and handed to the mutation instead of being pre-created.
 *
 * Returns null for an option this dialog cannot act on.
 */
function getReassignRecipientFields(
  option: WorkflowAssignmentSelection
): Pick<Apollo.ReassignRecipientInput, 'assignedUserId' | 'email' | 'permissionId'> | null {
  switch (option.__typename) {
    case 'WorkflowPermission':
      return { permissionId: option.id };
    case 'User':
      return { assignedUserId: option.id };
    case 'NewCollaborator':
      /**
       * Roles are only offered as options in template workflows, and this dialog runs against a
       * launched one, so a role is unreachable here.
       */
      return option.type === 'email' ? { email: option.text } : null;
    case 'AlineAi':
    case 'UserDefinedRole':
      return null;
  }
}
